import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useHuntStore } from "@/stores/hunt";
import type { SearchRequest, SearchResponse } from "@/types";

export function useSearch() {
  const qc = useQueryClient();
  return useMutation<SearchResponse, ApiError, SearchRequest>({
    mutationFn: async (req) => {
      const data = await apiFetch<SearchResponse>("/api/search", { json: req });
      // Commit BOTH the result and the searched params to the store here, on
      // success only (NEW-1): a failed search must not overwrite the persisted
      // form/result that ⭐ save-search and an F5 restore read. Captured inside the
      // mutationFn so it survives the user navigating away before it resolves (a
      // mutate-level onSuccess would be dropped on unmount).
      const store = useHuntStore.getState();
      store.setResult(data);
      store.setForm({ query: req.query ?? "", preset: req.preset ?? "", filters: req.filters ?? {} });
      return data;
    },
    // A search writes a history row server-side — refresh History so it's not stale
    // for up to the global staleTime (FE-5).
    onSuccess: () => qc.invalidateQueries({ queryKey: ["searches"] }),
    onError: (err) => {
      if (err.status === 401) return; // already redirecting to /login
      if (err.status === 429) {
        const ra = err.body.retry_after;
        toast.error(ra ? `Лимит GitHub. Подождите ~${ra}s.` : "Лимит GitHub — подождите.");
        return;
      }
      toast.error(err.message);
    },
  });
}
